'use client'

import {
    SiteBannerPlacement,
    createSiteBanner,
    getSiteBanner,
    getSitePlacements,
    updateSiteBanner,
    uploadSiteBannerImage,
} from '@/lib/api/marketing/site-banners-api'
import { useImageCrop } from '@/components/image-crop'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
    MdArrowBack,
    MdDelete,
    MdImage,
    MdSave,
    MdUpload,
    MdViewCarousel,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

/** `datetime-local` não aceita ISO com fuso; converte para o formato do input. */
function toInputValue(value: string | null | undefined): string {
    if (!value) return ''
    const date = new Date(value)
    const offset = date.getTimezoneOffset() * 60000
    return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

function toIsoOrNull(value: string): string | null {
    if (!value) return null
    return new Date(value).toISOString()
}

interface ImageFieldProps {
    id: string
    label: string
    hint: string
    value: string
    uploading: boolean
    onUpload: (file: File) => void
    onChange: (value: string) => void
    onClear: () => void
}

function ImageField({
    id,
    label,
    hint,
    value,
    uploading,
    onUpload,
    onChange,
    onClear,
}: ImageFieldProps) {
    const inputRef = useRef<HTMLInputElement>(null)

    return (
        <div className="form-control w-full">
            <label className="label pb-1" htmlFor={id}>
                <span className="label-text font-medium">{label}</span>
            </label>

            <div className="border-base-300 bg-base-200/40 flex flex-col gap-4 rounded-lg border border-dashed p-4">
                {value ? (
                    <img
                        src={value}
                        alt={label}
                        className="bg-base-200 max-h-48 w-full rounded object-contain"
                    />
                ) : (
                    <div className="text-base-content/30 flex h-32 items-center justify-center">
                        <MdImage className="text-5xl" />
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                    >
                        {uploading ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <MdUpload className="text-lg" />
                        )}
                        Enviar imagem
                    </button>
                    {value && (
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm text-error"
                            onClick={onClear}
                        >
                            <MdDelete className="text-lg" />
                            Remover
                        </button>
                    )}
                </div>

                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => {
                        const file = event.target.files?.[0]
                        if (file) onUpload(file)
                        event.target.value = ''
                    }}
                />

                <input
                    id={id}
                    type="url"
                    className="input input-bordered input-sm w-full"
                    placeholder="Ou cole a URL da imagem"
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                />
            </div>

            <label className="label pt-1">
                <span className="label-text-alt text-base-content/60">{hint}</span>
            </label>
        </div>
    )
}

export default function SiteBannerFormPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const { canCreate, canUpdate } = useResourcePermissions(
        'marketing',
        'site_banners',
    )
    const canSave = isEditing ? canUpdate : canCreate

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    // Banner de tela grande é largo; o de celular, mais alto. Proporções livres
    // com atalhos, porque cada posição do site tem um formato.
    const desktopCrop = useImageCrop({
        title: 'Ajustar banner',
        aspects: [
            { label: 'Livre' },
            { label: 'Panorâmica (3:1)', value: 3 },
            { label: 'Larga (21:9)', value: 21 / 9 },
            { label: 'Paisagem (16:9)', value: 16 / 9 },
        ],
        output: { maxWidth: 1920 },
    })
    const mobileCrop = useImageCrop({
        title: 'Ajustar banner do celular',
        aspects: [
            { label: 'Livre' },
            { label: 'Quadrada', value: 1 },
            { label: 'Retrato (4:5)', value: 4 / 5 },
            { label: 'Paisagem (16:9)', value: 16 / 9 },
        ],
        output: { maxWidth: 1080 },
    })
    const [uploadingDesktop, setUploadingDesktop] = useState(false)
    const [uploadingMobile, setUploadingMobile] = useState(false)

    const [placements, setPlacements] = useState<SiteBannerPlacement[]>([])

    const [placement, setPlacement] = useState('hero-home')
    const [name, setName] = useState('')
    const [imageUrl, setImageUrl] = useState('')
    const [imageMobileUrl, setImageMobileUrl] = useState('')
    const [alt, setAlt] = useState('')
    const [link, setLink] = useState('')
    const [linkNewTab, setLinkNewTab] = useState(false)
    const [startsAt, setStartsAt] = useState('')
    const [endsAt, setEndsAt] = useState('')
    const [active, setActive] = useState(true)

    useEffect(() => {
        loadPlacements()
    }, [])

    useEffect(() => {
        if (isEditing) loadBanner()
    }, [id])

    const loadPlacements = async () => {
        try {
            const res = await getSitePlacements()
            if (res.success && Array.isArray(res.result)) {
                setPlacements(res.result)
            }
        } catch {
            toast.error('Erro ao carregar os espaços do site')
        }
    }

    const loadBanner = async () => {
        setLoading(true)
        try {
            const res = await getSiteBanner(parseInt(id!, 10))
            if (res.success && res.result) {
                const banner = res.result
                setPlacement(banner.placement)
                setName(banner.name)
                setImageUrl(banner.imageUrl)
                setImageMobileUrl(banner.imageMobileUrl ?? '')
                setAlt(banner.alt ?? '')
                setLink(banner.link ?? '')
                setLinkNewTab(banner.linkNewTab)
                setStartsAt(toInputValue(banner.startsAt))
                setEndsAt(toInputValue(banner.endsAt))
                setActive(banner.active)
            } else {
                toast.error('Banner não encontrado')
                navigate('/marketing/site-banners')
            }
        } catch {
            toast.error('Erro ao carregar o banner')
            navigate('/marketing/site-banners')
        } finally {
            setLoading(false)
        }
    }

    const selectedPlacement = useMemo(
        () => placements.find((item) => item.key === placement),
        [placements, placement],
    )

    const handleUpload = async (rawFile: File, target: 'desktop' | 'mobile') => {
        const crop = target === 'desktop' ? desktopCrop : mobileCrop
        const file = await crop.cropFile(rawFile)
        if (!file) return

        const setUploading =
            target === 'desktop' ? setUploadingDesktop : setUploadingMobile

        setUploading(true)
        try {
            const res = await uploadSiteBannerImage(file)
            if (res.success && res.result?.url) {
                if (target === 'desktop') setImageUrl(res.result.url)
                else setImageMobileUrl(res.result.url)
                toast.success('Imagem enviada')
            } else {
                toast.error(res.error || 'Erro ao enviar a imagem')
            }
        } catch {
            toast.error('Erro ao enviar a imagem')
        } finally {
            setUploading(false)
        }
    }

    const handleSave = async () => {
        if (!name.trim()) {
            toast.warning('Informe o nome do banner')
            return
        }
        if (!imageUrl.trim()) {
            toast.warning('Envie a imagem do banner')
            return
        }
        if (startsAt && endsAt && new Date(startsAt) > new Date(endsAt)) {
            toast.warning('O início da exibição precisa ser anterior ao fim')
            return
        }

        const payload = {
            placement,
            name: name.trim(),
            imageUrl: imageUrl.trim(),
            imageMobileUrl: imageMobileUrl.trim() || null,
            alt: alt.trim() || null,
            link: link.trim() || null,
            linkNewTab,
            active,
            startsAt: toIsoOrNull(startsAt),
            endsAt: toIsoOrNull(endsAt),
        }

        setSaving(true)
        try {
            const res = isEditing
                ? await updateSiteBanner(parseInt(id!, 10), payload)
                : await createSiteBanner(payload)

            if (res.success) {
                toast.success(isEditing ? 'Banner atualizado' : 'Banner criado')
                navigate('/marketing/site-banners')
            } else {
                toast.error(res.error || 'Erro ao salvar o banner')
            }
        } catch {
            toast.error('Erro ao salvar o banner')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    return (
        <div className="mx-auto max-w-5xl space-y-6 p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate('/marketing/site-banners')}
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <MdViewCarousel className="text-secondary text-3xl" />
                    <div>
                        <h1 className="text-2xl font-bold">
                            {isEditing ? 'Editar banner' : 'Novo banner'}
                        </h1>
                        <p className="text-base-content/60 text-sm">
                            Banner do site institucional mediarsaude.com.br
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <label className="label cursor-pointer gap-3">
                        <span className="label-text font-medium">
                            {active ? 'Ativo' : 'Inativo'}
                        </span>
                        <input
                            type="checkbox"
                            className="toggle toggle-secondary"
                            checked={active}
                            onChange={(event) => setActive(event.target.checked)}
                        />
                    </label>
                    <button
                        className="btn btn-secondary"
                        onClick={handleSave}
                        disabled={saving || !canSave}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdSave className="text-lg" />
                        )}
                        Salvar
                    </button>
                </div>
            </div>

            {/* Identificação */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body gap-6">
                    <h2 className="text-lg font-bold">Identificação</h2>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="form-control w-full">
                            <label className="label pb-1" htmlFor="banner-name">
                                <span className="label-text font-medium">
                                    Nome *
                                </span>
                            </label>
                            <input
                                id="banner-name"
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex.: Campanha de setembro"
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                            />
                            <label className="label pt-1">
                                <span className="label-text-alt text-base-content/60">
                                    Usado apenas para você identificar no painel
                                </span>
                            </label>
                        </div>

                        <div className="form-control w-full">
                            <label className="label pb-1" htmlFor="banner-placement">
                                <span className="label-text font-medium">
                                    Espaço no site *
                                </span>
                            </label>
                            <select
                                id="banner-placement"
                                className="select select-bordered w-full"
                                value={placement}
                                onChange={(event) =>
                                    setPlacement(event.target.value)
                                }
                            >
                                {placements.map((item) => (
                                    <option key={item.key} value={item.key}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                            <label className="label pt-1">
                                <span className="label-text-alt text-base-content/60">
                                    {selectedPlacement
                                        ? `${selectedPlacement.description} Proporção sugerida: ${selectedPlacement.recommendedRatio}.`
                                        : 'Escolha onde o banner aparece'}
                                </span>
                            </label>
                        </div>
                    </div>

                    {selectedPlacement && !selectedPlacement.multiple && (
                        <div className="alert alert-info">
                            <span>
                                Este espaço exibe um banner por vez: se houver mais
                                de um ativo, o site mostra o primeiro da ordem.
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Artes */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body gap-6">
                    <h2 className="text-lg font-bold">Artes</h2>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <ImageField
                            id="banner-image"
                            label="Imagem principal *"
                            hint="Exibida em telas grandes. Máximo de 5 MB."
                            value={imageUrl}
                            uploading={uploadingDesktop}
                            onUpload={(file) => handleUpload(file, 'desktop')}
                            onChange={setImageUrl}
                            onClear={() => setImageUrl('')}
                        />

                        <ImageField
                            id="banner-image-mobile"
                            label="Imagem para celular"
                            hint="Opcional. Sem ela, o celular usa a imagem principal."
                            value={imageMobileUrl}
                            uploading={uploadingMobile}
                            onUpload={(file) => handleUpload(file, 'mobile')}
                            onChange={setImageMobileUrl}
                            onClear={() => setImageMobileUrl('')}
                        />
                    </div>

                    <div className="form-control w-full">
                        <label className="label pb-1" htmlFor="banner-alt">
                            <span className="label-text font-medium">
                                Texto alternativo
                            </span>
                        </label>
                        <input
                            id="banner-alt"
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Descreva a imagem para leitores de tela"
                            value={alt}
                            onChange={(event) => setAlt(event.target.value)}
                        />
                        <label className="label pt-1">
                            <span className="label-text-alt text-base-content/60">
                                Ajuda na acessibilidade e no posicionamento em
                                buscadores. Vazio usa o nome do banner.
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Destino e agendamento */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body gap-6">
                    <h2 className="text-lg font-bold">Destino e exibição</h2>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="form-control w-full">
                            <label className="label pb-1" htmlFor="banner-link">
                                <span className="label-text font-medium">
                                    Link de destino
                                </span>
                            </label>
                            <input
                                id="banner-link"
                                type="url"
                                className="input input-bordered w-full"
                                placeholder="https://... (opcional)"
                                value={link}
                                onChange={(event) => setLink(event.target.value)}
                            />
                            <label className="label pt-1">
                                <span className="label-text-alt text-base-content/60">
                                    Sem link, o banner fica apenas ilustrativo
                                </span>
                            </label>
                        </div>

                        <div className="form-control w-full justify-center">
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={linkNewTab}
                                    onChange={(event) =>
                                        setLinkNewTab(event.target.checked)
                                    }
                                    disabled={!link}
                                />
                                <span className="label-text font-medium">
                                    Abrir o link em uma nova aba
                                </span>
                            </label>
                        </div>

                        <div className="form-control w-full">
                            <label className="label pb-1" htmlFor="banner-starts-at">
                                <span className="label-text font-medium">
                                    Início da exibição
                                </span>
                            </label>
                            <input
                                id="banner-starts-at"
                                type="datetime-local"
                                className="input input-bordered w-full"
                                value={startsAt}
                                onChange={(event) => setStartsAt(event.target.value)}
                            />
                            <label className="label pt-1">
                                <span className="label-text-alt text-base-content/60">
                                    Vazio entra no ar assim que for ativado
                                </span>
                            </label>
                        </div>

                        <div className="form-control w-full">
                            <label className="label pb-1" htmlFor="banner-ends-at">
                                <span className="label-text font-medium">
                                    Fim da exibição
                                </span>
                            </label>
                            <input
                                id="banner-ends-at"
                                type="datetime-local"
                                className="input input-bordered w-full"
                                value={endsAt}
                                onChange={(event) => setEndsAt(event.target.value)}
                            />
                            <label className="label pt-1">
                                <span className="label-text-alt text-base-content/60">
                                    Vazio fica no ar até ser desativado
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="alert">
                        <span>
                            O site guarda o conteúdo por até 2 minutos. Depois de
                            salvar, a mudança aparece dentro desse intervalo.
                        </span>
                    </div>
                </div>
            </div>
            {desktopCrop.cropDialog}
            {mobileCrop.cropDialog}
        </div>
    )
}
